import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { Button, Col, Form, Modal, Radio, Row, message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ContactTypeEnum } from '@/core/domain/people/Suppliers/Suppliers.types';
import {
  StoneOrderOriginType,
  StoneOrderPaymentType,
  StoneOrderInstallmentType,
  StoneOrderItem,
  StoneOrderInstallment,
  StoneOrderCreateRequest,
  StoneOrderPayerType,
} from '@/core/domain/stone/orders/orders.types';
import {
  useStoneOrderCreate,
  useStoneOrderHasOpenMutate,
} from '@/core/domain/stone/orders/orders.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import { useCustomerContactsMutation } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { useInsurerContactListMutate } from '@/core/domain/people/InsurerContact/InsurersContact.hooks';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { DocumentTypePaymentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';

interface Props {
  installments: StoneOrderInstallment[];
  opened: boolean;
  onClose: VoidFunction;
  device?: StoneDevice;
  data: {
    paymentType?: DocumentTypePaymentType;
    id: number;
    stoneAccountId?: number;
    originType: StoneOrderOriginType;
    customerId?: number;
    insurerId?: number;
    companyId?: number;
    displayName: string;
    items: StoneOrderItem[];
    planAccountId?: number;
    documentTypeId?: number;
  };
}

interface FormValues {
  type: StoneOrderPaymentType.credit;
  installment_type: StoneOrderInstallmentType.issuer;
}

export default function StoneOrderCreateModal({
  installments,
  opened,
  onClose,
  device,
  data,
}: Props) {
  const customerDetail = useCustomerReduced();
  const customerContacts = useCustomerContactsMutation();
  const insurerDetail = useInsurerMutation();
  const insuerContacts = useInsurerContactListMutate();

  const [isWaintingOrderOpened, setIsWaintingOrderOpened] = useState(false);
  const [isWaintingOrderProccess, setIsWaintingOrderProccess] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [form] = Form.useForm<FormValues>();

  const requiredField = useDefaultEmptyFieldError();

  const stoneOrderCreate = useStoneOrderCreate();
  const stoneHasOrderOpened = useStoneOrderHasOpenMutate();

  useEffect(() => {
    if (!opened) {
      handleClose();
    }
  }, [opened]);

  async function getCustomer(customerId: number) {
    const customer = await customerDetail.mutateAsync(customerId);

    if (!customer) {
      message.error(`Cliente informado não foi encontrado`);
      return;
    }

    const contacts = await customerContacts.mutateAsync(customerId);

    const customerEmailContact = contacts.find((contact) =>
      contact.detailList.some(
        (item) => item.active && item.contactType === ContactTypeEnum.email,
      ),
    );

    if (!customerEmailContact) {
      message.error(
        `Necessário definir um email de contato para o cliente "${customer.name}"`,
      );
      return;
    }

    const customerEmail = customerEmailContact.detailList.find(
      (item) => item.active && item.contactType === ContactTypeEnum.email,
    );

    return {
      id: customerId,
      email: customerEmail?.value || '',
      name: customer.name,
      type: StoneOrderPayerType.CUSTOMER,
    };
  }

  async function getInsuer(insurerId: number) {
    const insurer = await insurerDetail.mutateAsync(insurerId);

    if (insurer) {
      const contactDetails = await insuerContacts.mutateAsync(insurerId);

      const constacts = contactDetails?.find((detail) =>
        detail.detailList.find(
          (item) => item.contactType === ContactType.Email,
        ),
      )?.detailList;

      const insurerEmail = constacts?.find(
        (contact) => contact.contactType === ContactType.Email,
      )?.value;

      if (!insurerEmail) {
        message.error(
          `Necessário definir um email de contato para o cliente "${insurer.name}"`,
        );
        return;
      }

      return {
        id: insurerId,
        email: insurerEmail,
        name: insurer.name,
        type: StoneOrderPayerType.INSURER,
      };
    }
  }

  const handleGenerateFinancialStone = useCallback(async () => {
    try {
      setIsFormLoading(true);
      const values = await form.validateFields();

      if (!isWaintingOrderOpened && device?.id) {
        const result = await stoneHasOrderOpened.mutateAsync(device?.id);

        if (result.hasOpenOrder) {
          setIsFormLoading(false);
          return Modal.confirm({
            width: 500,
            type: 'warning',
            title: 'Existe um pedido em processamento',
            content:
              'Para iniciar um novo pedido, é preciso aguardar a conclusão do pedido atual ou cancelá-lo.',
            okText: 'Aguardar e Gerar pedido',
            onOk: () => setIsWaintingOrderOpened(true),
          });
        }
      }

      setIsWaintingOrderOpened(false);

      if (data.companyId && data.stoneAccountId && device?.id) {
        let payer: StoneOrderCreateRequest['payer'] | undefined;

        if (data.customerId) {
          payer = await getCustomer(data.customerId);
        } else if (data.insurerId) {
          payer = await getInsuer(data.insurerId);
        } else {
          payer = undefined;
        }

        if (!payer) {
          return message.error(`Não foi encontrado os dados do pagador`);
        }

        await stoneOrderCreate.mutateAsync({
          payer,
          items: data.items,
          installments: installments,
          originType: data.originType,
          originId: data.id,
          stoneAccountId: data.stoneAccountId,
          companyId: data?.companyId,
          planAccountId: data.planAccountId as number,
          documentTypeId: data.documentTypeId as number,
          deviceIds: [device?.id],
          poi_payment_settings: {
            display_name: data.displayName,
            payment_setup: {
              type: values.type,
              installments: installments.length,
              installment_type: values.installment_type,
            },
          },
        });

        setIsWaintingOrderProccess(true);
      } else {
        return message.warning(
          `Está faltando dados para a geração de pedido: "${
            (!data.companyId ? 'Empresa' : '') ||
            (!data.stoneAccountId ? 'ID da conta Stone' : '') ||
            (!device?.id ? 'Dispositivo' : '')
          }"`,
        );
      }
    } catch {}

    setIsFormLoading(false);
  }, [data, installments, isWaintingOrderOpened]);

  function handleClose() {
    setIsWaintingOrderProccess(false);
    form.resetFields();
    setIsFormLoading(false);
    onClose();
  }

  const isLoading =
    isFormLoading ||
    stoneOrderCreate.isLoading ||
    customerDetail.isLoading ||
    customerContacts.isLoading;

  return (
    <Modal
      open={opened}
      onCancel={handleClose}
      onOk={handleGenerateFinancialStone}
      title="Gerar financeiro com a Stone"
      okText="Enviar pedido"
      maskClosable={false}
      closable={!isLoading}
      confirmLoading={isLoading}
      destroyOnClose
    >
      {isWaintingOrderOpened && (
        <ModalLoadingOverlay
          style={{ position: 'fixed', top: 0, left: 0 }}
          messages={[
            <div style={{ marginLeft: 24 }}>
              <div>Aguardando pedido atual em processamento...</div>
              <Button
                onClick={() => {
                  setIsWaintingOrderOpened(false);
                  setIsFormLoading(false);
                }}
              >
                Sair da espera
              </Button>
            </div>,
          ]}
        />
      )}

      {(stoneOrderCreate.isLoading ||
        customerDetail.isLoading ||
        customerContacts.isLoading) && (
        <ModalLoadingOverlay
          style={{ position: 'fixed', top: 0, left: 0 }}
          messages={['Gerando pedido de pagamento na Stone...']}
        />
      )}

      {isWaintingOrderProccess && (
        <ModalLoadingOverlay
          style={{ position: 'fixed', top: 0, left: 0 }}
          messages={[
            <div style={{ marginLeft: 24 }}>
              <div>Processando pedido de pagamento com a Stone...</div>
              <div style={{ fontSize: 14, color: 'darkgray' }}>
                Você pode sair da tela enquando é feito o processamento com a
                Stone
              </div>
              <Button onClick={handleClose}>Sair da espera</Button>
            </div>,
          ]}
        />
      )}

      <Form form={form} layout="vertical" preserve={false}>
        <p>Informe os dados abaixo para configurar o financeiro com a Stone</p>

        <Row gutter={[24, 16]}>
          <Col span={24}>
            <strong>Dispositivo Stone:</strong>
            <div>{device?.serialNumber}</div>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="type"
              label="Tipo de pagamento"
              rules={[requiredField]}
              initialValue={data.paymentType?.toLowerCase()}
              getValueFromEvent={() => data.paymentType?.toLowerCase()}
            >
              <Radio.Group buttonStyle="solid">
                <Radio.Button value={StoneOrderPaymentType.debit}>
                  Débito
                </Radio.Button>
                <Radio.Button value={StoneOrderPaymentType.credit}>
                  Crédito
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="installment_type"
              label="Tipo de parcelamento"
              rules={[requiredField]}
              initialValue={StoneOrderInstallmentType.merchant}
            >
              <Radio.Group buttonStyle="solid" disabled={isLoading}>
                <Radio.Button value={StoneOrderInstallmentType.merchant}>
                  Lojista
                </Radio.Button>
                <Radio.Button value={StoneOrderInstallmentType.issuer}>
                  Emissor
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
